import type {FileSummary} from 'folder-items/FileConstants';
import {getOmniboxAllowedTypes} from 'omnibox/omniboxConstants';
import type {OmniboxAttachmentsResponse, OmniboxResponse, RecentSearch} from 'omnibox/omniboxTypes';
import * as Net from 'util/Net';

export interface ProjectFolderTypeaheadItem {
  kind: 'project' | 'folder';
  apiId: string;
  name: string;
  projectName?: string;
  depth?: number;
  parentFolderName?: string;
  // Parent folder's apiId, or the project's apiId for a top-level folder. Absent on projects.
  parentApiId?: string;
}

export interface UserTypeaheadItem {
  apiId: string;
  name: string;
  handle: string;
  isSelf: boolean;
}

function buildOmniboxRequestBody(data: {
  query: string;
  requestId?: string;
  activeWorkspaceTabId?: string;
  openWorkspaceTabIds?: Array<string>;
  schemaApiIds?: Array<string>;
  objectTypes?: Array<string>;
  projectApiIds?: Array<string>;
  folderApiIds?: Array<string>;
  authorApiIds?: Array<string>;
}): object {
  return {
    ...data,
    allowedTypes: getOmniboxAllowedTypes(),
    // intentionally en-US to match the rest of our prompting
    userLocalTime: new Date().toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }),
    userTimezone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

/**
 * Stream omnibox results via Ably. Makes a regular POST request that publishes
 * partial results to the user's Ably channel as they become available.
 * Returns a promise that resolves with the final complete results.
 */
export function streamOmniboxQuery(data: {
  query: string;
  requestId: string;
  activeWorkspaceTabId?: string;
  openWorkspaceTabIds?: Array<string>;
  schemaApiIds?: Array<string>;
  objectTypes?: Array<string>;
  projectApiIds?: Array<string>;
  folderApiIds?: Array<string>;
  authorApiIds?: Array<string>;
}): Promise<OmniboxResponse> {
  return Net.postJSONPromise({
    url: '/1/api/omnibox/stream',
    data: buildOmniboxRequestBody(data),
  });
}

/**
 * Fetch attachment content matches for the query. Fired in parallel with streamOmniboxQuery so
 * attachment retrieval never blocks the main entity results; populates the attachments section.
 */
export function fetchOmniboxAttachments(data: {
  query: string;
  schemaApiIds?: Array<string>;
  objectTypes?: Array<string>;
  projectApiIds?: Array<string>;
  folderApiIds?: Array<string>;
  authorApiIds?: Array<string>;
}): Promise<OmniboxAttachmentsResponse> {
  return Net.postJSONPromise({
    url: '/1/api/omnibox/attachments',
    data: {...data, allowedTypes: getOmniboxAllowedTypes()},
  });
}

export function fetchProjectFolderTypeahead(
  query?: string,
  parentApiId?: string
): Promise<Array<ProjectFolderTypeaheadItem>> {
  const params = new URLSearchParams();
  if (query) {
    params.set('query', query);
  }
  if (parentApiId) {
    params.set('parentApiId', parentApiId);
  }
  const queryString = params.toString();
  return Net.getJSONPromise(`/1/api/omnibox/project-folder-typeahead${queryString ? `?${queryString}` : ''}`);
}

export function fetchUserTypeahead(query?: string): Promise<Array<UserTypeaheadItem>> {
  return Net.getJSONPromise(
    `/1/api/omnibox/user-typeahead${query ? `?query=${encodeURIComponent(query)}` : ''}`
  );
}

export function fetchRecentItems(): Promise<{data: Array<FileSummary>}> {
  return Net.getJSONPromise('/1/api/users/me/recent/');
}

export function fetchRecentSearches(): Promise<Array<RecentSearch>> {
  return Net.getJSONPromise('/1/api/omnibox/recent-searches');
}

export function recordSearch(query: string, openedApiId?: string, openedObjectType?: string): Promise<void> {
  return Net.postJSONPromise({
    url: '/1/api/omnibox/record-search',
    data: {query, openedApiId, openedObjectType},
  });
}
